/**
 * v16.17 Phase C.28 — Match URL slug utilities.
 *
 * SEO-friendly URL: `?match=bk-1` thay vì `?match={UUID}`. FE luôn ưu tiên
 * write slug format (từ match_code), backward-compat với UUID cũ khi read.
 */
import { useEffect, useState } from "react";
import { api } from "./api";
import * as governor from "./networkGovernor";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// HAR-19 (Feb 2026) — Offline burst-storm fix: RAM memo cho slug → UUID.
// Trước đây mỗi remount LivestreamTab bắn 1 request /matches/resolve, khi
// offline vẫn cố gắng (edgeApi/api sẽ throw isOffline nhưng request vẫn
// chiếm slot governor). Memo per-process giữ mapping slug→uuid trong RAM
// nguyên vòng đời SPA — offline reconnect KHÔNG gây burst nữa.
const _RESOLVE_MEMO = new Map(); // key `${tid}:${slug}` -> uuid

function _memoKey(tid, raw) {
  return `${tid || ""}:${(raw || "").toLowerCase()}`;
}
export function _clearResolveMemo() { _RESOLVE_MEMO.clear(); }  // test-only

/** Kiểm tra chuỗi có phải UUID không. */
export function isUuid(s) {
  return typeof s === "string" && UUID_RE.test(s);
}

/** Convert match_code (BK-1, TK-2, C-R1#1) → slug URL-safe (bk-1, tk-2, c-r1-1).
 *  v16.17 Phase C.36 — Đổi `#` → `-` để KHÔNG bị dính round + match (bug:
 *  "C-R1#1" trước đây strip `#` thành "c-r11", ambiguous với "C-R11"). */
export function matchCodeToSlug(code) {
  if (!code) return "";
  return String(code)
    .toLowerCase()
    .replace(/#/g, "-")   // C-R1#1 → c-r1-1
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/** v16.17 Phase C.37 — Slug hoá tiếng Việt (bỏ dấu + trim length).
 *  "Đội Alpha 5.1" → "doi-alpha-51". */
function _slugText(txt, maxLen) {
  if (!txt) return "";
  const stripped = String(txt)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d");
  const slug = stripped
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const trimmed = maxLen ? slug.slice(0, maxLen) : slug;
  return trimmed.replace(/-+$/, "");
}

/**
 * Trả về URL param cho `?match=`. Slug bao gồm:
 *   {match_code} - {teamA} - vs - {teamB} - {event}
 * để URL SEO-friendly + human readable. Team + event là hậu tố phụ, BE
 * resolve chỉ cần prefix match_code là đủ. Nếu 2 trận cùng match_code
 * (khác event), hậu tố event giúp phân biệt.
 *
 * Fallback UUID nếu không có match_code.
 * v16.17 Phase C.37.
 */
export function matchToUrlParam(match) {
  if (!match) return "";
  const codeSlug = matchCodeToSlug(match.match_code || "");
  if (!codeSlug) return match.id || "";
  const teamA = _slugText(match.team_a?.name, 20);
  const teamB = _slugText(match.team_b?.name, 20);
  const eventSlug = _slugText(match.event_name, 30);
  // v21.45 (PROD fix) — match_code có thể TRÙNG giữa nhiều nội dung trong cùng
  // giải (VD "A-R1#1" ở cả 3 event). Slug CHỈ có mã → resolve ra TRẬN SAI
  // (ambiguous → lấy nội dung đầu tiên). Nếu KHÔNG có team & KHÔNG có event để
  // phân biệt → GIỮ UUID (luôn điều hướng đúng trận), KHÔNG xuất slug chỉ-mã.
  if (!teamA && !teamB && !eventSlug) return match.id || "";
  const parts = [codeSlug];
  if (teamA && teamB) parts.push(teamA, "vs", teamB);
  else if (teamA) parts.push(teamA);
  else if (teamB) parts.push(teamB);
  if (eventSlug) parts.push(eventSlug);
  return parts.join("-");
}

/**
 * Hook resolve `?match=<value>` (UUID hoặc slug) → UUID thật.
 *
 * • Nếu value là UUID → return luôn (không network).
 * • Nếu value là slug → gọi BE `/api/public/tournaments/{tid}/matches/resolve/{slug}`.
 * • Nếu resolve fail → return null + isError=true.
 */
export function useResolvedMatchId(rawParam, tournamentId) {
  const [state, setState] = useState({
    id: rawParam && isUuid(rawParam) ? rawParam : null,
    isResolving: !!(rawParam && !isUuid(rawParam)),
    isError: false,
  });

  useEffect(() => {
    if (!rawParam) {
      setState({ id: null, isResolving: false, isError: false });
      return undefined;
    }
    if (isUuid(rawParam)) {
      setState({ id: rawParam, isResolving: false, isError: false });
      return undefined;
    }
    if (!tournamentId) {
      setState({ id: null, isResolving: false, isError: true });
      return undefined;
    }
    // HAR-19 — RAM memo hit: bypass network hoàn toàn.
    const memoK = _memoKey(tournamentId, rawParam);
    const cached = _RESOLVE_MEMO.get(memoK);
    if (cached) {
      setState({ id: cached, isResolving: false, isError: false });
      return undefined;
    }
    // HAR-19 — Offline short-circuit: KHÔNG bắn request khi confirmed offline
    // (governor sẽ throw ở interceptor nhưng vẫn tốn cycle + có thể race với
    // reconnect burst). Trả state đặc biệt để LivestreamTab render skeleton.
    if (governor.isOffline && governor.isOffline()) {
      setState({ id: null, isResolving: false, isError: false, isOffline: true });
      return undefined;
    }
    let cancelled = false;
    setState({ id: null, isResolving: true, isError: false });
    api
      .get(`/public/tournaments/${encodeURIComponent(tournamentId)}/matches/resolve/${encodeURIComponent(rawParam)}`)
      .then((r) => {
        if (cancelled) return;
        const resolvedId = r?.data?.id || null;
        if (resolvedId) _RESOLVE_MEMO.set(memoK, resolvedId);
        setState({
          id: resolvedId,
          isResolving: false,
          isError: !resolvedId,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ id: null, isResolving: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [rawParam, tournamentId]);

  return state;
}
